import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SearchBar from "../components/SearchBar";
import {
  CheckCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  StarIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/solid";

export default function Home() {
  // --- Testimonials state (simple auto-play) ---
  const testimonials = [
    {
      name: "Jordan P.",
      city: "Kelowna",
      text: "Driver was early, tracked our delayed flight, and had space for 6 skis + bags. Zero stress.",
    },
    {
      name: "Mei L.",
      city: "West Kelowna",
      text: "Clean Transit van, child seats ready, and door-to-door to Whistler. Worth every dollar.",
    },
    {
      name: "Ravi K.",
      city: "Revelstoke",
      text: "Booked a 12-passenger shuttle for our group to Big White—smooth ride and super professional.",
    },
  ];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % testimonials.length);
    }, 4500);
    return () => clearInterval(id);
  }, [testimonials.length]);
  const go = (dir) =>
    setIndex((i) => (i + dir + testimonials.length) % testimonials.length);

  return (
    <Layout>
      <div className="container mx-auto">
        {/* Book a Ride */}
        <div id="bookaride" className="mb-8">
          {/* SearchBar now shows results inline in its own white overlay box */}
          <SearchBar />
        </div>

        {/* WHY CHOOSE US (inline) */}
        <section id="why" className="py-8 sm:py-12">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Why choose KAR Shuttle
            </h2>
            <p className="text-sm sm:text-base text-gray-400 mt-1">
              Shared, pay-per-seat shuttles with room for all your gear.
            </p>

            <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mt-6">
              {[
                {
                  title: "Pay per seat (shared)",
                  desc: "Book just the seats you need. Perfect for solos, pairs, and small groups.",
                  Icon: UsersIcon,
                },
                {
                  title: "Live seat availability",
                  desc: "See remaining seats and choose departures that fit your schedule.",
                  Icon: ClockIcon,
                },
                {
                  title: "Licensed, insured, professional",
                  desc: "Commercial insurance and vetted drivers for every route.",
                  Icon: ShieldCheckIcon,
                },
                {
                  title: "Transparent pricing",
                  desc: "Taxes shown up front. No surprise fees for skis/boards.",
                  Icon: CheckCircleIcon,
                },
                {
                  title: "Clean & maintained fleet",
                  desc: "Transit vans configured for people + luggage and ski bags.",
                  Icon: WrenchScrewdriverIcon,
                },
                {
                  title: "Groups & private charters",
                  desc: "Need the whole vehicle? Ask for a private quote anytime.",
                  Icon: BriefcaseIcon,
                },
              ].map(({ title, desc, Icon }) => (
                <div
                  key={title}
                  className="rounded-2xl bg-zinc-900/40 border border-white/5 p-5 sm:p-6 shadow-[0_1px_0_rgba(255,255,255,0.04)] hover:shadow-lg hover:shadow-black/20 transition"
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0 rounded-xl bg-white/5 p-3">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{title}</h3>
                      <p className="text-gray-400 mt-1 text-sm leading-relaxed">
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DESTINATIONS: Ski resorts */}
        <section id="destinations" className="py-8 sm:py-12 scroll-mt-28">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                  Ski resort shuttles
                </h2>
              </div>
            </div>

            <div className="grid mt-6 gap-4 sm:gap-6 grid-cols-2 lg:grid-cols-3">
              {[
                {
                  name: "Big White",
                  subtitle: "Kelowna • Family favorite",
                  img: "/bg-mountain.png",
                },
                {
                  name: "SilverStar",
                  subtitle: "Vernon • Nordic + Alpine",
                  img: "/silverstar.png",
                },
                {
                  name: "Revelstoke Mountain Resort",
                  subtitle: "Revelstoke • Steeps",
                  img: "/apexmountain.png",
                },
              ].map((d) => (
                <div
                  key={d.name}
                  className="group relative overflow-hidden rounded-2xl bg-zinc-900/40 border border-white/5 shadow-[0_1px_0_rgba(255,255,255,0.04)] text-left"
                >
                  <img
                    src={d.img}
                    alt={d.name}
                    className="h-40 sm:h-48 w-full object-cover opacity-80 group-hover:opacity-100 transition"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 p-4">
                    <div className="text-lg font-semibold">{d.name}</div>
                    <div className="text-xs text-gray-300">{d.subtitle}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FLEET: Ford shuttles/vans */}
        <section id="fleet" className="py-8 sm:py-12">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                  Our shuttle fleet
                </h2>
                <p className="text-sm sm:text-base text-gray-400 mt-1">
                  Shared, per-seat services on spacious vans. Private charters available.
                </p>
              </div>
            </div>

            <div className="grid mt-6 gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  name: "Ford Transit 15-Passenger",
                  tag: "Shared shuttle • up to 13 seats",
                  img: "/fordTransit.png",
                  features: ["Ski/board storage", "Heated cabin", "Child seats on request"],
                },
              ].map((c) => (
                <div
                  key={c.name}
                  className="group rounded-2xl overflow-hidden bg-zinc-900/40 border border-white/5 shadow-[0_1px_0_rgba(255,255,255,0.04)]"
                >
                  <div className="relative">
                    <img
                      src={c.img}
                      alt={c.name}
                      className="h-44 sm:h-48 w-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3 text-[11px] bg-black/60 backdrop-blur rounded-full px-2 py-1">
                      Shared • Per seat
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="font-semibold flex items-center gap-2">
                      {c.name}
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
                        {c.route}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">{c.tag}</div>
                    <div className="flex items-center gap-3 mt-3 text-xs">
                      <span className="rounded-full bg-white/5 border border-white/10 px-2 py-1">
                        Gear included
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {c.features.map((f) => (
                        <span
                          key={f}
                          className="text-[11px] rounded-full bg-white/5 border border-white/10 px-2 py-1"
                        >
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-400 mt-4">
              * Seat counts and pricing are examples. Final availability and fare shown at checkout.
              Private group/charter pricing on request.
            </p>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="py-8 sm:py-14">
          <div className="container mx-auto px-4">
            <div className="flex items-end justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
                  What riders say
                </h2>
                <p className="text-sm sm:text-base text-gray-400 mt-1">
                  Real trips. Real smiles.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => go(-1)}
                  aria-label="Previous"
                  className="rounded-xl border border-white/10 px-3 py-2 hover:bg-white/5"
                >
                  ‹
                </button>
                <button
                  onClick={() => go(1)}
                  aria-label="Next"
                  className="rounded-xl border border-white/10 px-3 py-2 hover:bg-white/5"
                >
                  ›
                </button>
              </div>
            </div>

            <div className="relative overflow-hidden">
              {/* Track */}
              <div
                className="flex transition-transform duration-700 ease-out will-change-transform"
                style={{ transform: `translateX(-${index * 100}%)` }}
              >
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className="flex-[0_0_100%] min-w-full px-2"  // EXACT 100% slide width
                  >
                    <div className="rounded-2xl bg-zinc-900/40 border border-white/5 p-6 sm:p-8 mx-auto max-w-3xl shadow-[0_1px_0_rgba(255,255,255,0.04)]">
                      <div className="flex items-start gap-3">
                        <div className="shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-white/20 to-white/5 grid place-items-center">
                          <StarIcon className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="text-base sm:text-lg leading-relaxed">“{t.text}”</p>
                          <div className="mt-3 text-sm text-gray-400">
                            — {t.name} • {t.city}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-4">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    className={`h-2 w-2 rounded-full ${i === index ? 'bg-white' : 'bg-white/30'}`}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
